import React, { useState, useEffect, useRef } from 'react';
import AiRichTextEditor from './AiRichTextEditor';
import {
  Globe, LayoutTemplate, Plus, Trash2, Edit3,
  MessageSquare, Sparkles, Send, Bot, CheckCircle2,
  Settings, Save, Eye, User as UserIcon, Loader2, ArrowLeft, MousePointerClick,
  ChevronDown, Image as ImageIcon, Type, Link as LinkIcon, Palette, X, FileText, AlertCircle, Upload, Monitor, Tablet, Smartphone, Building
} from 'lucide-react';
import PublicVacancyView from './PublicVacancyView';
import { BrandingConfig, User, WebsiteSection, WebsiteSectionType, Page, UserRole, Client } from '../types';
import { updateCareerSiteSections, generateSiteContent } from '../services/geminiService';
import {
  supabase_get_client_branding,
  supabase_update_client_branding,
  supabase_get_clients,
  supabase_upload_website_asset
} from '../services/supabaseService';

const DEFAULT_CONFIG: BrandingConfig = {
  primaryColor: '#e51b23',
  secondaryColor: '#1E40AF',
  font: 'Inter, sans-serif',
  logoUrl: 'https://api.dicebear.com/7.x/initials/svg?seed=TN',
  heroImage: '',
  heroTitle: '',
  heroSubtitle: '',
  aboutTitle: '',
  aboutText: '',
  cultureItems: [],
  stories: [],
  teamMembers: [],
  pages: [
    {
      id: 'home',
      title: 'Home',
      slug: '/',
      isHome: true,
      sections: [
        {
          id: 'hero_1',
          type: 'hero',
          title: 'Welkom bij ons bedrijf',
          subtitle: 'Ontdek jouw droombaan',
          content: { imageUrl: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=2000' },
          isVisible: true,
          styles: { backgroundColor: '#0f172a', textColor: '#ffffff', padding: 'large' }
        },
        {
          id: 'vacancies_1',
          type: 'vacancies',
          title: 'Openstaande Vacatures',
          isVisible: true,
          styles: { backgroundColor: '#f8fafc', textColor: '#0f172a', padding: 'large' }
        }
      ]
    },
    {
      id: 'over_ons',
      title: 'Over ons',
      slug: '/over-ons',
      isHome: false,
      sections: [
        {
          id: 'hero_about',
          type: 'hero',
          title: 'Over Ons',
          subtitle: 'Leer ons beter kennen',
          content: { imageUrl: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=2000' },
          isVisible: true,
          styles: { backgroundColor: '#1e293b', textColor: '#ffffff', padding: 'small' }
        },
        {
          id: 'text_image_about',
          type: 'text_image',
          title: 'Onze Missie',
          content: { text: 'Wij streven ernaar om de beste werkplek te creëren door innovatie en samenwerking te stimuleren.', imageUrl: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=2000' },
          isVisible: true,
          styles: { backgroundColor: '#ffffff', textColor: '#0f172a', padding: 'normal' }
        }
      ]
    },
    {
      id: 'verhalen',
      title: 'Verhalen',
      slug: '/verhalen',
      isHome: false,
      sections: [
        {
          id: 'gallery_verhalen',
          type: 'gallery',
          title: 'Werken bij ons',
          subtitle: 'Een impressie van onze cultuur',
          isVisible: true,
          styles: { backgroundColor: '#f8fafc', textColor: '#0f172a', padding: 'normal' }
        },
        {
          id: 'features_verhalen',
          type: 'features',
          title: 'Succesverhalen',
          subtitle: 'Ontdek hoe onze medewerkers groeien.',
          isVisible: true,
          styles: { backgroundColor: '#ffffff', textColor: '#0f172a', padding: 'normal' }
        }
      ]
    },
    {
      id: 'team',
      title: 'Team',
      slug: '/team',
      isHome: false,
      sections: [
        {
          id: 'team_1',
          type: 'team',
          title: 'Ontmoet het Team',
          subtitle: 'De mensen achter ons succes',
          isVisible: true,
          styles: { backgroundColor: '#ffffff', textColor: '#0f172a', padding: 'large' },
          content: {
            teamMembers: [
              { id: 'tm_1', name: 'Jan Jansen', role: 'CEO', imageUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Jan', description: 'Oprichter en drijvende kracht.' },
              { id: 'tm_2', name: 'Lieke Visser', role: 'CTO', imageUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Lieke', description: 'Technisch brein.' }
            ]
          }
        }
      ]
    },
    {
      id: 'contact',
      title: 'Contact',
      slug: '/contact',
      isHome: false,
      sections: [
        {
          id: 'contact_1',
          type: 'contact',
          title: 'Neem Contact Op',
          subtitle: 'Heb je vragen? Laat het ons weten.',
          isVisible: true,
          styles: { backgroundColor: '#f8fafc', textColor: '#0f172a', padding: 'large' }
        }
      ]
    },
    {
      id: 'vacatures',
      title: 'Vacatures',
      slug: '/vacatures',
      isHome: false,
      sections: [
        {
          id: 'vacatures_page_1',
          type: 'vacancies',
          title: 'Grijp je kans',
          subtitle: 'Bekijk al onze openstaande vacatures hieronder.',
          isVisible: true,
          styles: { backgroundColor: '#ffffff', textColor: '#0f172a', padding: 'large' }
        }
      ]
    }
  ]
};

interface CareersSiteEditorProps {
  user?: User | null;
}

const CareersSiteEditor: React.FC<CareersSiteEditorProps> = ({ user }) => {
  const [appConfig, setAppConfig] = useState<BrandingConfig>(DEFAULT_CONFIG);
  const [activePageId, setActivePageId] = useState<string>('home');
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [viewportMode, setViewportMode] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [inspectorWidth, setInspectorWidth] = useState(400);

  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(user?.clientId || null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [chatMessages, setChatMessages] = useState<{ role: 'user' | 'ai', text: string }[]>([
    { role: 'ai', text: 'Hoi! Ik ben Froukje. Wil je dat ik een "Over Ons" sectie toevoeg of de hero tekst aanpas?' }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Page Manager State
  const [showPageManager, setShowPageManager] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPageSlug, setNewPageSlug] = useState('');

  // Footer Editor State
  const [showFooterEditor, setShowFooterEditor] = useState(false);

  // Upload State
  const [isUploading, setIsUploading] = useState<string | null>(null);

  // Fullscreen Preview State
  const [isFullscreenPreview, setIsFullscreenPreview] = useState(false);

  useEffect(() => {
    if (user?.role === UserRole.ADMIN || user?.role === UserRole.RECRUITER) {
      const fetchClients = async () => {
        const data = await supabase_get_clients();
        setClients(data || []);
        if (!selectedClientId && data && data.length > 0) {
          setSelectedClientId(data[0].id);
        }
      };
      fetchClients();
    } else if (user?.clientId) {
      const fetchMyClient = async () => {
        const data = await supabase_get_clients(user.clientId);
        setClients(data || []);
        if (!selectedClientId) {
          setSelectedClientId(user.clientId);
        }
      };
      fetchMyClient();
    }
  }, [user]);

  useEffect(() => {
    const fetchConfig = async () => {
      if (selectedClientId) {
        setIsLoading(true);
        const savedConfig = await supabase_get_client_branding(selectedClientId);
        if (savedConfig && Object.keys(savedConfig).length > 0) {
          // Normalize pages
          let migratedPages = savedConfig.pages || [];
          if (migratedPages.length === 0 && savedConfig.sections && savedConfig.sections.length > 0) {
            migratedPages = [{ id: 'home', title: 'Home', slug: '/', isHome: true, sections: savedConfig.sections }];
          }
          if (migratedPages.length === 0) {
            migratedPages = DEFAULT_CONFIG.pages;
          }
          setAppConfig({ ...DEFAULT_CONFIG, ...savedConfig, pages: migratedPages });
        } else {
          setAppConfig(DEFAULT_CONFIG);
        }
        setIsLoading(false);
      }
    };
    fetchConfig();
  }, [selectedClientId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const saveConfig = async () => {
    console.log("FORCE_HMR_UPDATE_v2: Save payload triggering...", selectedClientId);
    if (selectedClientId) {
      setIsSaving(true);
      try {
        await supabase_update_client_branding(selectedClientId, appConfig);
        alert('Site Opgeslagen!');
      } catch (err: any) {
        console.error("Save Error:", err);
        alert("Fout bij opslaan: " + (err.message || "Onbekende fout"));
      } finally {
        setIsSaving(false);
      }
    }
  };

  const activePage = appConfig.pages?.find(p => p.id === activePageId) || appConfig.pages?.[0];
  const activeSections = activePage?.sections || [];

  const updateActivePageSections = (newSections: WebsiteSection[]) => {
    setAppConfig(prev => ({
      ...prev,
      pages: prev.pages?.map(p => p.id === activePageId ? { ...p, sections: newSections } : p)
    }));
  };

  const handleAddPage = async () => {
    if (!newPageTitle.trim() || !newPageSlug.trim()) return;

    let formattedSlug = newPageSlug.toLowerCase().trim().replace(/\s+/g, '-');
    if (!formattedSlug.startsWith('/')) {
      formattedSlug = '/' + formattedSlug;
    }

    const newPage: Page = {
      id: `page_${Date.now()}`,
      title: newPageTitle,
      slug: formattedSlug,
      isHome: false,
      sections: []
    };

    const newConfig = {
      ...appConfig,
      pages: [...(appConfig.pages || []), newPage]
    };
    
    setAppConfig(newConfig);
    setActivePageId(newPage.id);

    setNewPageTitle('');
    setNewPageSlug('');
    setShowPageManager(false); // Close the modal to dive into the new page

    if (selectedClientId) {
      try { await supabase_update_client_branding(selectedClientId, newConfig); }
      catch (e) { console.error('Add page save err:', e); }
    }
  };

  const handleDeletePage = async (pageId: string) => {
    if (confirm('Weet je zeker dat je deze pagina wilt verwijderen?')) {
      const updatedPages = (appConfig.pages || []).filter(p => p.id !== pageId);
      const newConfig = { ...appConfig, pages: updatedPages };
      
      setAppConfig(newConfig);
      
      if (activePageId === pageId) {
        const homePage = updatedPages.find(p => p.isHome);
        setActivePageId(homePage?.id || updatedPages[0]?.id || null);
      }

      if (selectedClientId) {
        setIsSaving(true);
        try { await supabase_update_client_branding(selectedClientId, newConfig); }
        catch (e) { console.error('Page deletion save err: ', e); }
        finally { setIsSaving(false); }
      }
    }
  };

  const handleChatSubmit = async () => {
    if (!chatInput.trim() || isAiGenerating) return;

    const input = chatInput;
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', text: input }]);
    setIsAiGenerating(true);

    try {
      const newSections = await updateCareerSiteSections(activeSections, input, appConfig);
      if (newSections && Array.isArray(newSections)) {
        updateActivePageSections(newSections);
        setChatMessages(prev => [...prev, { role: 'ai', text: '✅ Ik heb de secties voor je bijgewerkt! Bekijk het resultaat hiernaast.' }]);
      }
    } catch (err) {
      console.error(err);
      setChatMessages(prev => [...prev, { role: 'ai', text: 'Mijn excuses, er ging iets mis bij het updaten van de structuur.' }]);
    } finally {
      setIsAiGenerating(false);
    }
  };

  const addSection = (type: WebsiteSectionType) => {
    const newSection: WebsiteSection = {
      id: `${type}_${Math.random().toString(36).substr(2, 9)} `,
      type,
      title: `Nieuwe ${type} sectie`,
      isVisible: true,
      styles: { backgroundColor: '#ffffff', textColor: '#0f172a', padding: 'large' },
      content: {}
    };
    updateActivePageSections([...activeSections, newSection]);
    setSelectedSectionId(newSection.id);
  };

  const updateSection = (id: string, updates: Partial<WebsiteSection>) => {
    updateActivePageSections(activeSections.map(s => s.id === id ? { ...s, ...updates } : s));
  };

  const deleteSection = async (id: string) => {
    if (confirm('Sectie verwijderen?')) {
      const newSections = activeSections.filter(s => s.id !== id);
      const newConfig = {
        ...appConfig,
        pages: appConfig.pages?.map(p => p.id === activePageId ? { ...p, sections: newSections } : p)
      };
      
      setAppConfig(newConfig);
      if (selectedSectionId === id) setSelectedSectionId(null);

      if (selectedClientId) {
        try { await supabase_update_client_branding(selectedClientId, newConfig); }
        catch (e) { console.error('Section deletion save err: ', e); }
      }
    }
  };

  const extractDominantColor = (imageUrl: string) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.src = imageUrl;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      try {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        let r = 0, g = 0, b = 0, count = 0;
        // Sample pixels, skipping fully transparent ones
        for (let i = 0; i < imageData.length; i += 16) {
          if (imageData[i + 3] > 0) {
            r += imageData[i];
            g += imageData[i + 1];
            b += imageData[i + 2];
            count++;
          }
        }
        if (count > 0) {
          r = Math.floor(r / count);
          g = Math.floor(g / count);
          b = Math.floor(b / count);
          const hex = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;

          // Generate a darker variant for secondary color
          const r2 = Math.max(0, r - 40);
          const g2 = Math.max(0, g - 40);
          const b2 = Math.max(0, b - 40);
          const hexDark = `#${((1 << 24) + (r2 << 16) + (g2 << 8) + b2).toString(16).slice(1)}`;

          setAppConfig(prev => ({ ...prev, primaryColor: hex, secondaryColor: hexDark }));
        }
      } catch (e) {
        console.warn("Could not extract color due to CORS", e);
      }
    };
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, callback: (url: string) => void, fieldId: string, isLogo = false) => {
    if (!e.target.files?.[0]) return;
    setIsUploading(fieldId);
    try {
      const url = await supabase_upload_website_asset(e.target.files[0]);
      if (url) {
        callback(url);
        if (isLogo) {
          extractDominantColor(url);
        }
      }
    } catch (err) {
      console.error(err);
      alert("Upload mislukt.");
    } finally {
      setIsUploading(null);
    }
  };

  if (isLoading) {
    return <div className="h-screen flex items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-indigo-600" size={48} /></div>;
  }

  const selectedSection = activeSections.find(s => s.id === selectedSectionId);

  return (
    <div className="h-screen flex flex-col bg-slate-50 overflow-hidden font-sans">
      {/* TOP NAVBAR */}
      <div className="h-14 bg-white border-b border-slate-200 px-6 flex items-center justify-between shrink-0 shadow-sm z-20">
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white"><LayoutTemplate size={16} /></div>
          <h1 className="font-bold text-slate-800 text-sm tracking-tight">Werfplaats Site Editor</h1>

          {(user?.role === UserRole.ADMIN || user?.role === UserRole.RECRUITER) ? (
            <select
              className="text-xs text-slate-500 bg-transparent outline-none cursor-pointer border border-slate-200 rounded px-2 py-1"
              value={selectedClientId || ''}
              onChange={(e) => setSelectedClientId(e.target.value)}
            >
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          <select
            className="text-sm border border-slate-200 rounded-md px-3 py-1.5 focus:border-indigo-500"
            value={activePageId}
            onChange={(e) => setActivePageId(e.target.value)}
          >
            {appConfig.pages?.map(p => (
              <option key={p.id} value={p.id}>{p.title} ({p.slug})</option>
            ))}
          </select>
          <button
            onClick={() => setShowPageManager(true)}
            className="flex items-center gap-1.5 text-xs font-bold bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-md hover:bg-indigo-100 transition-colors border border-indigo-100"
          >
            <Settings size={14} /> Beheer Pagina's
          </button>

          <button
            onClick={saveConfig}
            disabled={isSaving}
            className="ml-4 flex items-center gap-2 bg-indigo-600 text-white px-5 py-1.5 rounded-md text-sm font-bold shadow-sm hover:bg-indigo-700 transition-colors disabled:opacity-50"
          >
            {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Opslaan
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* PANE 1: LEFT SIDEBAR - FULL HEIGHT FROUKJE AI CHAT */}
        {!isFullscreenPreview && (
          <div className="w-80 bg-white border-r border-slate-200 flex flex-col z-10 shadow-sm shrink-0">
            <div className="px-4 py-4 border-b border-slate-100 flex items-center gap-3 bg-indigo-50/50">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white shadow-sm"><Sparkles size={16} /></div>
              <div>
                <h2 className="font-bold text-sm text-slate-800 tracking-tight">Froukje AI Assistent</h2>
                <p className="text-[10px] text-slate-500">Jouw website bouwer</p>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 text-xs space-y-4 custom-scrollbar bg-slate-50/30">
              {chatMessages.map((msg, i) => (
                <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 shadow-sm ${msg.role === 'user' ? 'bg-slate-200 text-slate-600' : 'bg-white text-indigo-600 border border-slate-100'}`}>
                    {msg.role === 'user' ? <UserIcon size={14} /> : <img src="https://api.dicebear.com/7.x/bottts/svg?seed=Froukje&backgroundColor=e0e7ff" alt="AI" className="w-8 h-8 rounded-full" />}
                  </div>
                  <div className={`p-3 rounded-2xl max-w-[85%] leading-relaxed ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-tr-none' : 'bg-white border border-slate-200 text-slate-700 shadow-sm rounded-tl-none'}`}>
                    {msg.text}
                  </div>
                </div>
              ))}
              {isAiGenerating && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-white border border-slate-100 text-indigo-600 flex items-center justify-center shadow-sm"><Loader2 size={14} className="animate-spin" /></div>
                  <div className="px-4 py-3 rounded-2xl bg-white border border-slate-200 text-slate-400 rounded-tl-none flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce"></div>
                    <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce delay-75"></div>
                    <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce delay-150"></div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <div className="p-4 bg-white border-t border-slate-200">
              <div className="relative flex items-center shadow-sm rounded-xl border border-slate-200 bg-slate-50 focus-within:bg-white focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100 transition-all">
                <textarea
                  placeholder="Typ hier wat je wilt bouwen of aanpassen..."
                  className="w-full bg-transparent border-none text-xs rounded-xl pl-4 pr-12 py-3 outline-none min-h-[60px] resize-none custom-scrollbar"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleChatSubmit();
                    }
                  }}
                />
                <button onClick={handleChatSubmit} disabled={isAiGenerating || !chatInput.trim()} className="absolute right-2 bottom-2 w-8 h-8 bg-indigo-600 text-white rounded-lg flex items-center justify-center disabled:opacity-50 hover:bg-indigo-700 transition-colors shadow-sm">
                  <Send size={14} />
                </button>
              </div>
              <p className="text-[9px] text-center text-slate-400 mt-2">Druk op Enter om te verzenden, Shift+Enter voor nieuwe regel</p>
            </div>
          </div>
        )}

        {/* PANE 2: CENTER CANVAS - LIVE PREVIEW */}
        <div className={`flex-1 flex flex-col overflow-hidden relative ${isFullscreenPreview ? 'p-0 bg-white' : 'p-6 bg-slate-200/80 shadow-inner'}`}>
          {/* Viewport Controls */}
          <div className="absolute top-8 right-8 z-10 flex gap-2 bg-white/90 backdrop-blur p-1.5 rounded-lg shadow-sm border border-slate-200">
            {!isFullscreenPreview && (
              <div className="flex bg-slate-100 rounded border border-slate-200 shadow-inner mr-2 p-0.5">
                <button onClick={() => setViewportMode('desktop')} className={`px-2 py-1.5 rounded flex items-center justify-center transition-colors ${viewportMode === 'desktop' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`} title="Desktop">
                  <Monitor size={14} />
                </button>
                <button onClick={() => setViewportMode('tablet')} className={`px-2 py-1.5 rounded flex items-center justify-center transition-colors ${viewportMode === 'tablet' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`} title="Tablet">
                  <Tablet size={14} />
                </button>
                <button onClick={() => setViewportMode('mobile')} className={`px-2 py-1.5 rounded flex items-center justify-center transition-colors ${viewportMode === 'mobile' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`} title="Mobiel">
                  <Smartphone size={14} />
                </button>
              </div>
            )}
            <button
              onClick={() => setIsFullscreenPreview(!isFullscreenPreview)}
              className={`p-1.5 rounded flex items-center gap-1.5 text-xs font-bold transition-colors ${isFullscreenPreview ? 'bg-indigo-600 text-white shadow-inner' : 'text-slate-600 hover:bg-slate-100 hover:text-indigo-600'}`}
              title={isFullscreenPreview ? "Verklein Voorbeeld" : "Volledig Scherm Voorbeeld"}
            >
              <Globe size={16} /> {isFullscreenPreview && 'Sluiten'}
            </button>
          </div>

          <div className={`w-full h-full bg-white overflow-hidden flex flex-col ${isFullscreenPreview ? 'rounded-none border-none' : 'rounded-xl shadow-2xl border border-slate-300'}`}>
            {/* Browser mock topbar */}
            {!isFullscreenPreview && (
              <div className="bg-slate-100 px-4 py-3 flex items-center gap-4 border-b border-slate-200 shrink-0">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                  <div className="flex-1 max-w-md mx-auto bg-white rounded-full h-6 px-3 flex items-center text-[10px] text-slate-400 font-mono shadow-inner border border-slate-200 truncate space-x-1">
                    <Globe size={10} className="shrink-0 mr-1" />
                    <span className="truncate">
                      {appConfig.customDomain || `${clients.find(c => c.id === selectedClientId)?.name?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'careers'}.werftools.nl`}
                      {activePageId === 'home' ? '' : (appConfig.pages?.find(p=>p.id===activePageId)?.slug || `/${activePageId}`)}
                    </span>
                  </div>
                </div>
            )}

            <div className="flex-1 overflow-y-auto preview-container bg-transparent flex justify-center">
              <div
                className={`transition-all duration-300 min-h-full bg-white flex flex-col ${
                  isFullscreenPreview || viewportMode === 'desktop' ? 'w-full' : viewportMode === 'tablet' ? 'w-[768px] shadow-2xl border-x border-slate-200' : 'w-[375px] shadow-2xl border-x border-slate-200'
                }`}
              >
                {(() => {
                   const activeClient = clients.find(c => c.id === selectedClientId) || clients[0];
                   return (
                     <PublicVacancyView
                       clientId={selectedClientId || 'c1'}
                       propClient={activeClient}
                       previewClient={activeClient}
                       customBranding={appConfig}
                       activePageId={activePageId}
                     />
                   );
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* PANE 3: RIGHT SIDEBAR - INSPECTOR (SECTIONS & PROPERTIES) */}
        {!isFullscreenPreview && (
          <div 
            className="bg-white border-l border-slate-200 flex flex-col z-10 shadow-xl shrink-0 relative"
            style={{ width: inspectorWidth }}
          >
            {/* Draggable Resizer Handle */}
            <div
              className="absolute top-0 bottom-0 left-0 w-1.5 cursor-col-resize hover:bg-indigo-300 z-50 transition-colors bg-transparent group"
              onMouseDown={(e) => {
                e.preventDefault();
                const startX = e.pageX;
                const startWidth = inspectorWidth;
                
                const onMouseMove = (moveEvent: MouseEvent) => {
                  const delta = startX - moveEvent.pageX;
                  const newWidth = Math.max(300, Math.min(800, startWidth + delta));
                  setInspectorWidth(newWidth);
                };
                
                const onMouseUp = () => {
                  document.removeEventListener('mousemove', onMouseMove);
                  document.removeEventListener('mouseup', onMouseUp);
                };
                
                document.addEventListener('mousemove', onMouseMove);
                document.addEventListener('mouseup', onMouseUp);
              }}
            >
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-0.5 h-8 bg-slate-300 rounded group-hover:bg-indigo-500 transition-colors" />
            </div>

            {!selectedSection ? (
              // SECTIONS LIST VIEW
              <div className="flex-1 overflow-y-auto flex flex-col min-h-0">
                <div className="px-4 py-4 bg-white sticky top-0 border-b border-slate-100 z-10 flex flex-col gap-3">
                  <h3 className="text-sm font-bold text-slate-800">Secties op pagina</h3>
                  <div className="relative group w-full">
                    <button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white flex items-center justify-center gap-2 text-sm font-bold px-4 py-2.5 rounded-lg transition-colors shadow-sm">
                      <Plus size={16} /> Sectie Toevoegen
                    </button>
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 shadow-xl rounded-xl hidden group-hover:block z-50 overflow-hidden divide-y divide-slate-100">
                      {['hero', 'features', 'text_image', 'vacancies', 'gallery', 'faq', 'team', 'contact'].map(type => (
                        <button
                          key={type}
                          onClick={() => addSection(type as WebsiteSectionType)}
                          className="w-full text-left px-4 py-3 text-xs font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 capitalize transition-colors flex items-center justify-between"
                        >
                          {type.replace('_', ' ')}
                          <Plus size={14} className="opacity-50" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="p-3 space-y-2">
                  {activeSections.map((section, idx) => (
                    <div
                      key={section.id}
                      className="group flex items-center justify-between p-3 rounded-lg border border-slate-200 cursor-pointer transition-all hover:border-indigo-300 hover:shadow-sm bg-white"
                      onClick={() => setSelectedSectionId(section.id)}
                    >
                      <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-bold text-slate-800">{section.title || section.type}</span>
                        <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">{section.type.replace('_', ' ')}</span>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button className="p-1.5 text-slate-400 hover:text-indigo-600 rounded bg-slate-50 hover:bg-indigo-50"><Edit3 size={14} /></button>
                        <button onClick={(e) => { e.stopPropagation(); deleteSection(section.id); }} className="p-1.5 text-slate-400 hover:text-red-500 rounded bg-slate-50 hover:bg-red-50"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                  {activeSections.length === 0 && (
                    <div className="p-8 text-center flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl mt-4">
                      <LayoutTemplate size={24} className="text-slate-300" />
                      <p className="text-xs font-medium text-slate-500">Nog geen secties.</p>
                      <p className="text-[10px] text-slate-400">Vraag Froukje of voeg er handmatig een toe.</p>
                    </div>
                  )}
                </div>

                <div className="p-4 border-t border-slate-200 bg-slate-50 mt-auto">
                  <button
                    onClick={() => setShowFooterEditor(true)}
                    className="w-full bg-white border border-slate-200 hover:border-indigo-300 text-slate-700 hover:text-indigo-600 flex items-center justify-center gap-2 text-sm font-bold px-4 py-2.5 rounded-lg transition-colors shadow-sm"
                  >
                    <Palette size={16} /> Kop & Voettekst
                  </button>
                </div>
              </div>
            ) : (
              // SECTION PROPERTIES VIEW
              <div className="flex-1 overflow-y-auto flex flex-col min-h-0 bg-slate-50/30">
                <div className="h-14 flex items-center justify-between px-4 border-b border-slate-200 bg-white sticky top-0 z-10 shadow-sm shrink-0">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setSelectedSectionId(null)} className="p-1.5 text-slate-400 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors">
                      <ArrowLeft size={14} />
                    </button>
                    <h3 className="text-sm font-bold text-slate-800 truncate">{selectedSection.title || 'Sectie Aanpassen'}</h3>
                  </div>
                </div>
                <div className="flex-1 p-5 space-y-6 custom-scrollbar text-sm">
                  <div className="space-y-2">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><Type size={12} /> Titel</label>
                    <AiRichTextEditor
                      value={selectedSection.title || ''}
                      onChange={val => updateSection(selectedSection.id, { title: val })}
                      placeholder="Sectie titel..."
                      minHeight="60px"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><Type size={12} /> Subtitel / Intro</label>
                    <AiRichTextEditor
                      value={selectedSection.subtitle || ''}
                      onChange={val => updateSection(selectedSection.id, { subtitle: val })}
                      placeholder="Sectie subtitel of intro..."
                      minHeight="80px"
                    />
                  </div>

                  {['hero', 'text_image', 'features'].includes(selectedSection.type) && (
                    <div className="space-y-2">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><Type size={12} /> Hoofdtekst (met Froukje AI)</label>
                      <AiRichTextEditor
                        value={selectedSection.content?.text || ''}
                        onChange={(val) => updateSection(selectedSection.id, { content: { ...selectedSection.content, text: val } })}
                        placeholder="Typ of genereer tekst met AI..."
                        minHeight="150px"
                      />
                    </div>
                  )}

                  {['hero', 'gallery', 'text_image'].includes(selectedSection.type) && (
                    <div className="space-y-2">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><ImageIcon size={12} /> Afbeelding URL</label>
                      <div className="relative flex items-center gap-2">
                        <div className="relative flex-1">
                          <LinkIcon size={14} className="absolute left-3 top-2.5 text-slate-400" />
                          <input
                            type="text"
                            value={selectedSection.content?.imageUrl || ''}
                            onChange={e => updateSection(selectedSection.id, { content: { ...selectedSection.content, imageUrl: e.target.value } })}
                            className="w-full border border-slate-200 rounded-lg py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none shadow-sm transition-shadow bg-white"
                          />
                        </div>
                        <div className="relative shrink-0">
                          <label className="flex items-center gap-1 bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 px-3 py-2 rounded-lg cursor-pointer text-xs font-bold transition-colors">
                            {isUploading === 'content_image' ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                            Upload
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => updateSection(selectedSection.id, { content: { ...selectedSection.content, imageUrl: url } }), 'content_image')} />
                          </label>
                        </div>
                      </div>
                      {selectedSection.content?.imageUrl && (
                        <div className="mt-2 rounded-lg border border-slate-200 overflow-hidden h-24 bg-slate-100 flex items-center justify-center">
                          <img src={selectedSection.content.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                        </div>
                      )}
                    </div>
                  )}

                  {selectedSection.type === 'team' && (
                    <div className="space-y-4 pt-4 border-t border-slate-200">
                      <div className="flex items-center justify-between">
                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><UserIcon size={12} /> Teamleden ({(selectedSection.content?.teamMembers || []).length})</label>
                        <button
                          onClick={() => {
                            const currentMembers = selectedSection.content?.teamMembers || [];
                            const newMember = { id: `tm_${Date.now()}`, name: 'Nieuw Lid', role: 'Functie', imageUrl: '' };
                            updateSection(selectedSection.id, { content: { ...selectedSection.content, teamMembers: [...currentMembers, newMember] } });
                          }}
                          className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded hover:bg-indigo-100 transition-colors"
                        >
                          <Plus size={10} className="inline mr-1" /> Toevoegen
                        </button>
                      </div>
                      <div className="space-y-3">
                        {(selectedSection.content?.teamMembers || []).map((tm: any, index: number) => (
                          <div key={tm.id} className="p-3 bg-slate-50 border border-slate-200 rounded-lg shadow-sm space-y-3 relative group">
                            <button
                              onClick={() => {
                                const newMembers = [...(selectedSection.content?.teamMembers || [])];
                                newMembers.splice(index, 1);
                                updateSection(selectedSection.id, { content: { ...selectedSection.content, teamMembers: newMembers } });
                              }}
                              className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-500 bg-white hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100 shadow-sm border border-slate-100"
                            >
                              <Trash2 size={12} />
                            </button>

                            <div className="flex flex-col gap-2 pr-6">
                              <AiRichTextEditor
                                value={tm.name || ''}
                                onChange={(val) => {
                                  const newMembers = [...(selectedSection.content?.teamMembers || [])];
                                  newMembers[index] = { ...tm, name: val };
                                  updateSection(selectedSection.id, { content: { ...selectedSection.content, teamMembers: newMembers } });
                                }}
                                placeholder="Naam"
                                minHeight="40px"
                              />
                              <AiRichTextEditor
                                value={tm.role || ''}
                                onChange={(val) => {
                                  const newMembers = [...(selectedSection.content?.teamMembers || [])];
                                  newMembers[index] = { ...tm, role: val };
                                  updateSection(selectedSection.id, { content: { ...selectedSection.content, teamMembers: newMembers } });
                                }}
                                placeholder="Functie"
                                minHeight="40px"
                              />
                            </div>

                            <div className="flex items-center gap-2">
                              <input type="text" placeholder="Foto URL" value={tm.imageUrl || ''} onChange={(e) => {
                                const newMembers = [...(selectedSection.content?.teamMembers || [])];
                                newMembers[index] = { ...tm, imageUrl: e.target.value };
                                updateSection(selectedSection.id, { content: { ...selectedSection.content, teamMembers: newMembers } });
                              }} className="border border-slate-200 p-2 text-xs rounded-md w-full focus:border-indigo-500 outline-none bg-white" />
                              <label className="flex items-center gap-1 bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 px-3 py-2 rounded-md cursor-pointer text-xs font-bold transition-colors shadow-sm whitespace-nowrap">
                                {isUploading === `tm_img_${index}` ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                                Upload
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => {
                                  const newMembers = [...(selectedSection.content?.teamMembers || [])];
                                  newMembers[index] = { ...tm, imageUrl: url };
                                  updateSection(selectedSection.id, { content: { ...selectedSection.content, teamMembers: newMembers } });
                                }, `tm_img_${index}`)} />
                              </label>
                            </div>
                            <input type="text" placeholder="LinkedIn URL" value={tm.linkedin || ''} onChange={(e) => {
                              const newMembers = [...(selectedSection.content?.teamMembers || [])];
                              newMembers[index] = { ...tm, linkedin: e.target.value };
                              updateSection(selectedSection.id, { content: { ...selectedSection.content, teamMembers: newMembers } });
                            }} className="border border-slate-200 p-1.5 text-xs rounded-md w-full focus:border-indigo-500 outline-none" />
                            <input type="text" placeholder="LinkedIn URL" value={tm.linkedin || ''} onChange={(e) => {
                              const newMembers = [...(selectedSection.content?.teamMembers || [])];
                              newMembers[index] = { ...tm, linkedin: e.target.value };
                              updateSection(selectedSection.id, { content: { ...selectedSection.content, teamMembers: newMembers } });
                            }} className="border border-slate-200 p-1.5 text-xs rounded-md w-full focus:border-indigo-500 outline-none" />
                            <AiRichTextEditor
                              value={tm.description || ''}
                              onChange={(val) => {
                                const newMembers = [...(selectedSection.content?.teamMembers || [])];
                                newMembers[index] = { ...tm, description: val };
                                updateSection(selectedSection.id, { content: { ...selectedSection.content, teamMembers: newMembers } });
                              }}
                              placeholder="Korte bio/omschrijving..."
                              minHeight="80px"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedSection.type === 'vacancies' && (
                     <div className="space-y-4 pt-4 border-t border-slate-200">
                        <div className="flex items-center justify-between">
                           <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                              <Building size={12} className="text-slate-400" /> Opdrachtgever(s) Filter
                           </label>
                        </div>
                        <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg shadow-sm">
                           <p className="text-xs text-slate-500 mb-3">Laat leeg om alle zichtbare vacatures te tonen. Vink opdrachtgevers aan om de vacatures in dit blok te beperken tot specifieke klanten.</p>
                           <div className="space-y-1 max-h-48 overflow-y-auto custom-scrollbar p-2 border border-slate-100 rounded-md bg-white">
                              {clients.map((c: any) => (
                                 <label key={c.id} className="flex items-center gap-3 cursor-pointer group p-1.5 hover:bg-slate-50 rounded transition-colors">
                                    <input 
                                       type="checkbox" 
                                       checked={(selectedSection.content?.clientFilters || []).includes(c.id)}
                                       onChange={(e) => {
                                          const currentFilters = selectedSection.content?.clientFilters || [];
                                          let newFilters;
                                          if (e.target.checked) {
                                             newFilters = [...currentFilters, c.id];
                                          } else {
                                             newFilters = currentFilters.filter((id: string) => id !== c.id);
                                          }
                                          updateSection(selectedSection.id, { content: { ...selectedSection.content, clientFilters: newFilters } });
                                       }}
                                       className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 shadow-sm transition-colors cursor-pointer w-4 h-4"
                                    />
                                    <span className="text-sm font-medium text-slate-600 group-hover:text-slate-900 truncate flex-1">{c.name}</span>
                                 </label>
                              ))}
                              {clients.length === 0 && <p className="text-xs text-slate-400 italic">Geen opdrachtgevers gevonden.</p>}
                           </div>
                        </div>
                     </div>
                  )}

                  <div className="space-y-4 pt-6 border-t border-slate-200">
                    <h4 className="text-[10px] font-bold text-slate-800 uppercase tracking-widest flex items-center gap-1.5"><Palette size={14} className="text-indigo-500" /> Vormgeving & Lay-out</h4>

                    <div className="space-y-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                      {/* Padding Control */}
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-slate-600">Sectie Hoogte / Padding</label>
                        <select
                          className="w-full border border-slate-200 rounded-md p-2 text-xs focus:border-indigo-500 outline-none"
                          value={selectedSection.styles?.padding || 'normal'}
                          onChange={e => updateSection(selectedSection.id, { styles: { ...selectedSection.styles, padding: e.target.value as any } })}
                        >
                          <option value="py-8">Klein (py-8)</option>
                          <option value="py-16">Normaal (py-16)</option>
                          <option value="py-24">Groot (py-24)</option>
                          <option value="py-32">Extra Groot (py-32)</option>
                          <option value="py-0">Geen padding</option>
                        </select>
                      </div>

                      {/* Typography Controls */}
                      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-100">
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-slate-600">Lettertype</label>
                          <select
                            className="w-full border border-slate-200 rounded-md p-1.5 text-xs focus:border-indigo-500 outline-none"
                            value={selectedSection.styles?.fontFamily || 'font-sans'}
                            onChange={e => updateSection(selectedSection.id, { styles: { ...selectedSection.styles, fontFamily: e.target.value } })}
                          >
                            <option value="font-sans">Inter (Sans)</option>
                            <option value="font-serif">Merriweather (Serif)</option>
                            <option value="font-mono">Code (Mono)</option>
                          </select>
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-slate-600">Lettergrootte</label>
                          <select
                            className="w-full border border-slate-200 rounded-md p-1.5 text-xs focus:border-indigo-500 outline-none"
                            value={selectedSection.styles?.fontSize || 'text-base'}
                            onChange={e => updateSection(selectedSection.id, { styles: { ...selectedSection.styles, fontSize: e.target.value } })}
                          >
                            <option value="text-sm">Klein</option>
                            <option value="text-base">Normaal</option>
                            <option value="text-lg">Groot</option>
                            <option value="text-xl">Extra Groot</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-100">
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-slate-600">Subtitel Lettertype</label>
                          <select
                            className="w-full border border-slate-200 rounded-md p-1.5 text-xs focus:border-indigo-500 outline-none"
                            value={selectedSection.styles?.subtitleStyles?.fontFamily || 'font-sans'}
                            onChange={e => updateSection(selectedSection.id, { styles: { ...selectedSection.styles, subtitleStyles: { ...selectedSection.styles?.subtitleStyles, fontFamily: e.target.value } } })}
                          >
                            <option value="font-sans">Inter (Sans)</option>
                            <option value="font-serif">Merriweather (Serif)</option>
                            <option value="font-mono">Code (Mono)</option>
                          </select>
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-slate-600">Subtitel Grootte</label>
                          <select
                            className="w-full border border-slate-200 rounded-md p-1.5 text-xs focus:border-indigo-500 outline-none"
                            value={selectedSection.styles?.subtitleStyles?.fontSize || 'text-sm'}
                            onChange={e => updateSection(selectedSection.id, { styles: { ...selectedSection.styles, subtitleStyles: { ...selectedSection.styles?.subtitleStyles, fontSize: e.target.value } } })}
                          >
                            <option value="text-xs">Extra Klein</option>
                            <option value="text-sm">Klein</option>
                            <option value="text-base">Normaal</option>
                            <option value="text-lg">Groot</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-100">
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-slate-600">Tekst Schaduw</label>
                          <select
                            className="w-full border border-slate-200 rounded-md p-1.5 text-xs focus:border-indigo-500 outline-none"
                            value={selectedSection.styles?.textShadow || 'none'}
                            onChange={e => updateSection(selectedSection.id, { styles: { ...selectedSection.styles, textShadow: e.target.value } })}
                          >
                            <option value="none">Geen Schaduw</option>
                            <option value="drop-shadow-sm">Zacht</option>
                            <option value="drop-shadow-md">Normaal</option>
                            <option value="drop-shadow-lg">Hard</option>
                            <option value="drop-shadow-xl">Zwaar</option>
                          </select>
                        </div>
                      </div>

                      <div className="space-y-3 pt-3 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-600">Achtergrondkleur</label>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-slate-400 uppercase font-mono bg-slate-100 px-1.5 py-0.5 rounded">{selectedSection.styles?.backgroundColor || '#ffffff'}</span>
                            <input
                              type="color"
                              value={selectedSection.styles?.backgroundColor || '#ffffff'}
                              onChange={e => updateSection(selectedSection.id, { styles: { ...selectedSection.styles, backgroundColor: e.target.value } })}
                              className="w-8 h-8 rounded-md cursor-pointer border shadow-sm p-0.5 bg-white"
                            />
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-slate-600">Achtergrond Afbeelding URL</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              placeholder="https://..."
                              value={selectedSection.styles?.backgroundImage || ''}
                              onChange={e => updateSection(selectedSection.id, { styles: { ...selectedSection.styles, backgroundImage: e.target.value } })}
                              className="w-full border border-slate-200 rounded-md p-2 text-xs focus:border-indigo-500 outline-none"
                            />
                            <label className="flex items-center gap-1 bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 px-3 py-2 rounded-md cursor-pointer text-xs font-bold transition-colors shadow-sm whitespace-nowrap">
                              {isUploading === 'bg_image' ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                              Upload
                              <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => updateSection(selectedSection.id, { styles: { ...selectedSection.styles, backgroundImage: url } }), 'bg_image')} />
                            </label>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-600">Tekstkleur</label>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-slate-400 uppercase font-mono bg-slate-100 px-1.5 py-0.5 rounded">{selectedSection.styles?.textColor || '#000000'}</span>
                            <input
                              type="color"
                              value={selectedSection.styles?.textColor || '#000000'}
                              onChange={e => updateSection(selectedSection.id, { styles: { ...selectedSection.styles, textColor: e.target.value } })}
                              className="w-8 h-8 rounded-md cursor-pointer border shadow-sm p-0.5 bg-white"
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-600">Knop Achtergrondkleur</label>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-slate-400 uppercase font-mono bg-slate-100 px-1.5 py-0.5 rounded">{selectedSection.styles?.buttonColor || appConfig.primaryColor || '#e51b23'}</span>
                            <input
                              type="color"
                              value={selectedSection.styles?.buttonColor || appConfig.primaryColor || '#e51b23'}
                              onChange={e => updateSection(selectedSection.id, { styles: { ...selectedSection.styles, buttonColor: e.target.value } })}
                              className="w-8 h-8 rounded-md cursor-pointer border shadow-sm p-0.5 bg-white"
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-600">Knop Tekstkleur</label>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-slate-400 uppercase font-mono bg-slate-100 px-1.5 py-0.5 rounded">{selectedSection.styles?.buttonTextColor || '#ffffff'}</span>
                            <input
                              type="color"
                              value={selectedSection.styles?.buttonTextColor || '#ffffff'}
                              onChange={e => updateSection(selectedSection.id, { styles: { ...selectedSection.styles, buttonTextColor: e.target.value } })}
                              className="w-8 h-8 rounded-md cursor-pointer border shadow-sm p-0.5 bg-white"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* PAGE MANAGER OVERLAY */}
      {showPageManager && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600">
                  <FileText size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-800">Pagina Beheer</h2>
                  <p className="text-xs text-slate-500">Voeg nieuwe pagina's toe of verwijder ze</p>
                </div>
              </div>
              <button
                onClick={() => setShowPageManager(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 bg-white space-y-8">
              {/* Custom Domain Settings */}
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-5 space-y-3">
                <h3 className="text-sm font-bold text-indigo-900 flex items-center gap-2">
                  <Globe size={16} /> Koppel Eigen Domein
                </h3>
                <p className="text-xs text-indigo-700/80">Stel je eigen (sub)domeinnaam in (bijv. werfplaats.werftools.nl). Zorg dat je DNS record naar ons platform verwijst.</p>
                <div className="flex items-center gap-0">
                   <div className="flex items-center flex-1">
                      <span className="text-xs font-bold text-slate-500 bg-white px-3 py-2.5 border border-slate-200 rounded-l-lg border-r-0 h-[42px] flex items-center">https://</span>
                      <input 
                         type="text" 
                         placeholder="werfplaats.werftools.nl" 
                         value={appConfig.customDomain || ''} 
                         onChange={e => setAppConfig(prev => ({ ...prev, customDomain: e.target.value.toLowerCase().trim().replace(/^https?:\/\//, '').replace(/\/$/, '') }))}
                         className="w-full border border-slate-200 rounded-r-lg p-2.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all h-[42px] font-mono" 
                      />
                   </div>
                   <button 
                      onClick={() => {
                        saveConfig();
                        alert("Domein opgeslagen! Open de vernieuwde URL om de live versie te bekijken.");
                      }}
                      disabled={isSaving}
                      className="ml-3 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2 h-[42px] shrink-0"
                   >
                      {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Opslaan
                   </button>
                </div>
              </div>

              {/* Add New Page Form */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <Plus size={16} className="text-indigo-600" /> Nieuwe Pagina Aanmaken
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Pagina Titel</label>
                    <input
                      type="text"
                      placeholder="bijv: Over Ons"
                      value={newPageTitle}
                      onChange={e => setNewPageTitle(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg p-2.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">URL Slug</label>
                    <div className="flex items-center">
                      <span className="bg-slate-200 text-slate-500 px-3 py-2.5 rounded-l-lg border border-r-0 border-slate-200 text-sm font-mono leading-tight flex items-center min-h-[42px] max-w-[200px] truncate shrink-0">
                        {appConfig.customDomain || `${clients.find(c => c.id === selectedClientId)?.name?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'careers'}.werftools.nl`}
                      </span>
                      <input
                        type="text"
                        placeholder="/over-ons"
                        value={newPageSlug}
                        onChange={e => setNewPageSlug(e.target.value)}
                        className="w-full border border-slate-200 rounded-r-lg p-2.5 text-sm font-mono focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all min-h-[42px]"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end pt-2">
                  <button
                    onClick={handleAddPage}
                    disabled={!newPageTitle.trim() || !newPageSlug.trim()}
                    className="bg-indigo-600 text-white px-5 py-2 rounded-lg text-sm font-bold shadow-sm hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    Opslaan & Toevoegen <Plus size={16} />
                  </button>
                </div>
              </div>

              {/* List of Pages */}
              <div>
                <h3 className="text-sm font-bold text-slate-800 mb-4 px-1">Actieve Pagina's</h3>
                <div className="space-y-2">
                  {(appConfig.pages || []).map(p => (
                    <div key={p.id} className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:border-indigo-300 transition-colors group">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${p.isHome ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-500'}`}>
                          <Globe size={18} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-800 text-sm">{p.title}</span>
                            {p.isHome && <span className="bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">Home</span>}
                          </div>
                          <span className="text-slate-500 text-xs font-mono bg-slate-50 px-1.5 py-0.5 rounded mt-1 inline-block border border-slate-100">
                            {p.slug}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setActivePageId(p.id);
                            setShowPageManager(false);
                          }}
                          className="px-3 py-1.5 text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors"
                        >
                          Bewerken in Editor
                        </button>
                        {!p.isHome && (
                          <button
                            onClick={() => handleDeletePage(p.id)}
                            className="p-1.5 text-slate-400 hover:text-red-500 bg-slate-50 hover:bg-red-50 rounded-md transition-colors"
                            title="Pagina Verwijderen"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {(appConfig.pages || []).length === 1 && (
                  <div className="mt-4 p-4 bg-red-/50 border border-red- rounded-xl flex items-start gap-3">
                    <AlertCircle size={16} className="text-red-0 mt-0.5 shrink-0" />
                    <p className="text-xs text-red- leading-relaxed">
                      Je site heeft momenteel alleen een Home pagina. Wil je je menu uitbreiden? Maak hierboven extra pagina's aan zoals "Over Ons", "Onze Cultuur" of "Contact".
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end">
              <button
                onClick={() => setShowPageManager(false)}
                className="px-5 py-2 bg-white border border-slate-200 rounded-lg text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors shadow-sm"
              >
                Sluiten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FOOTER EDITOR OVERLAY */}
      {showFooterEditor && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600">
                  <Palette size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-800">Globale Website Instellingen</h2>
                  <p className="text-xs text-slate-500">Pas het palet, de fonts en globale blokken aan</p>
                </div>
              </div>
              <button
                onClick={() => setShowFooterEditor(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 bg-white space-y-8">

              {/* --- GLOBALE KLEUREN (PALETTE) --- */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-slate-800 border-b border-slate-100 pb-2 flex items-center gap-2">
                  <Palette size={16} className="text-indigo-500" /> Kleurenpalet
                </h3>
                <div className="grid grid-cols-5 gap-4">
                  {[
                     { key: 'primary', label: 'Primary' },
                     { key: 'sub1', label: 'Sub 1' },
                     { key: 'sub2', label: 'Sub 2' },
                     { key: 'sub3', label: 'Sub 3' },
                     { key: 'sub4', label: 'Sub 4' }
                  ].map(slot => (
                     <div key={slot.key} className="flex flex-col items-center gap-2">
                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">{slot.label}</label>
                        <input
                           type="color"
                           value={slot.key === 'primary' ? appConfig.palette?.primary || appConfig.primaryColor || '#e51b23' : (appConfig.palette as any)?.[slot.key] || '#ffffff'}
                           onChange={e => {
                               const val = e.target.value;
                               setAppConfig(prev => ({
                                  ...prev,
                                  ...(slot.key === 'primary' ? { primaryColor: val } : {}),
                                  palette: { ...(prev.palette || { primary: prev.primaryColor || '#e51b23', sub1: '#fff', sub2: '#fff', sub3: '#fff', sub4: '#fff' }), [slot.key]: val }
                               }));
                           }}
                           className="w-10 h-10 rounded-full cursor-pointer shadow-sm border border-slate-200 outline-none"
                        />
                     </div>
                  ))}
                </div>
              </div>

              {/* --- SOLLICITATIE KNOPPEN --- */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-slate-800 border-b border-slate-100 pb-2 flex items-center gap-2">
                  <MousePointerClick size={16} className="text-indigo-500" /> Sollicitatie Knoppen
                </h3>
                <div className="grid grid-cols-2 gap-6">
                   <div className="space-y-1.5">
                     <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Start Sollicitatie Knop</label>
                     <div className="flex gap-2">
                       <input type="color" value={appConfig.vacancyButtonColor || appConfig.primaryColor || '#e51b23'} onChange={e => setAppConfig(prev => ({...prev, vacancyButtonColor: e.target.value}))} className="w-10 h-10 rounded-lg shrink-0 cursor-pointer border shadow-sm p-1 bg-white"/>
                       <input type="text" value={appConfig.vacancyButtonColor || appConfig.primaryColor || '#e51b23'} onChange={e => setAppConfig(prev => ({...prev, vacancyButtonColor: e.target.value}))} className="flex-1 border border-slate-200 rounded-lg p-2.5 text-sm uppercase font-mono"/>
                     </div>
                   </div>
                   <div className="space-y-1.5">
                     <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">LinkedIn Knop</label>
                     <div className="flex gap-2">
                       <input type="color" value={appConfig.linkedinButtonColor || '#0077b5'} onChange={e => setAppConfig(prev => ({...prev, linkedinButtonColor: e.target.value}))} className="w-10 h-10 rounded-lg shrink-0 cursor-pointer border shadow-sm p-1 bg-white"/>
                       <input type="text" value={appConfig.linkedinButtonColor || '#0077b5'} onChange={e => setAppConfig(prev => ({...prev, linkedinButtonColor: e.target.value}))} className="flex-1 border border-slate-200 rounded-lg p-2.5 text-sm uppercase font-mono"/>
                     </div>
                   </div>
                </div>
              </div>

              {/* --- TYPOGRAFIE --- */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-slate-800 border-b border-slate-100 pb-2 flex items-center gap-2">
                  <Type size={16} className="text-indigo-500" /> Typografie & Lettertypes
                </h3>
                <div className="grid grid-cols-2 gap-6">
                   <div className="space-y-1.5">
                     <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Main Font</label>
                     <select value={appConfig.font || 'Roboto, sans-serif'} onChange={e => setAppConfig(prev => ({...prev, font: e.target.value}))} className="w-full border border-slate-200 rounded-lg p-2.5 text-sm outline-none">
                        <option value="Roboto, sans-serif">Roboto</option>
                        <option value="Inter, sans-serif">Inter</option>
                        <option value="Montserrat, sans-serif">Montserrat</option>
                        <option value="Open Sans, sans-serif">Open Sans</option>
                        <option value="Merriweather, serif">Merriweather</option>
                     </select>
                   </div>
                   <div className="space-y-1.5">
                     <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Globale Tekstkleur</label>
                     <div className="flex gap-2">
                       <input type="color" value={appConfig.fontColor || '#334155'} onChange={e => setAppConfig(prev => ({...prev, fontColor: e.target.value}))} className="w-10 h-10 rounded-lg shrink-0 cursor-pointer border shadow-sm p-1 bg-white"/>
                       <input type="text" value={appConfig.fontColor || '#334155'} onChange={e => setAppConfig(prev => ({...prev, fontColor: e.target.value}))} className="flex-1 border border-slate-200 rounded-lg p-2.5 text-sm uppercase font-mono"/>
                     </div>
                   </div>
                </div>
              </div>

              {/* --- HEADER SETTINGS --- */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-slate-800 border-b border-slate-100 pb-2 flex items-center gap-2">
                  <LayoutTemplate size={16} className="text-indigo-500" /> Header (Menu) Kleuren
                </h3>

                <div className="space-y-1.5 pb-4 border-b border-slate-100">
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Logo URL</label>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-slate-50 border border-slate-200 rounded flex items-center justify-center overflow-hidden shadow-inner shrink-0 p-1">
                      {appConfig.logoUrl ? (
                         <img src={appConfig.logoUrl} alt="Logo Preview" className="max-w-full max-h-full object-contain" />
                      ) : <ImageIcon className="text-slate-300" size={24} />}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input
                        type="url"
                        value={appConfig.logoUrl || ''}
                        onChange={e => setAppConfig(prev => ({ ...prev, logoUrl: e.target.value }))}
                        placeholder="https://jouw-logo.png"
                        className="w-full border border-slate-200 rounded-lg p-2.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all"
                      />
                      <label className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 px-3 py-1.5 rounded-md cursor-pointer text-xs font-bold transition-colors shadow-sm whitespace-nowrap">
                        {isUploading === 'logo' ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                        Upload Bestand
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => setAppConfig(prev => ({ ...prev, logoUrl: url })), 'logo', true)} />
                      </label>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wide flex justify-between">
                       Logo Padding (Witruimte) <span className="text-indigo-600">{appConfig.logoPadding || 0}px</span>
                    </label>
                    <input type="range" min="0" max="40" step="2" value={appConfig.logoPadding || 0} onChange={e => setAppConfig(prev => ({...prev, logoPadding: Number(e.target.value)}))} className="w-full accent-indigo-600 my-2" />
                    <p className="text-[10px] text-slate-400">Gebruik minder padding (0px) om het logo groter over de volledige header-hoogte te tonen.</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Header Achtergrond</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={appConfig.headerColor || '#0F172A'}
                        onChange={e => setAppConfig(prev => ({ ...prev, headerColor: e.target.value }))}
                        className="w-10 h-10 rounded-lg cursor-pointer border shadow-sm p-1 bg-white shrink-0"
                      />
                      <input
                        type="text"
                        value={appConfig.headerColor || '#0F172A'}
                        onChange={e => setAppConfig(prev => ({ ...prev, headerColor: e.target.value }))}
                        className="flex-1 border border-slate-200 rounded-lg p-2.5 text-sm uppercase font-mono focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Header Tekstkleur</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={appConfig.headerTextColor || '#FFFFFF'}
                        onChange={e => setAppConfig(prev => ({ ...prev, headerTextColor: e.target.value }))}
                        className="w-10 h-10 rounded-lg cursor-pointer border shadow-sm p-1 bg-white shrink-0"
                      />
                      <input
                        type="text"
                        value={appConfig.headerTextColor || '#FFFFFF'}
                        onChange={e => setAppConfig(prev => ({ ...prev, headerTextColor: e.target.value }))}
                        className="flex-1 border border-slate-200 rounded-lg p-2.5 text-sm uppercase font-mono focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6 mt-4">
                  <div className="space-y-1.5">
                     <label className="text-[10px] font-bold text-slate-500 uppercase">Header Lettertype</label>
                     <select value={appConfig.headerFont || ''} onChange={e => setAppConfig(prev => ({...prev, headerFont: e.target.value}))} className="w-full border border-slate-200 rounded-lg p-2 text-xs">
                        <option value="">-- Main Font --</option>
                        <option value="Roboto, sans-serif">Roboto</option>
                        <option value="Inter, sans-serif">Inter</option>
                        <option value="Montserrat, sans-serif">Montserrat</option>
                        <option value="Open Sans, sans-serif">Open Sans</option>
                     </select>
                  </div>
                  <div className="space-y-1.5">
                     <label className="text-[10px] font-bold text-slate-500 uppercase">Header Text Formaat</label>
                     <select value={appConfig.headerFontSize || 'text-xs'} onChange={e => setAppConfig(prev => ({...prev, headerFontSize: e.target.value}))} className="w-full border border-slate-200 rounded-lg p-2 text-xs">
                        <option value="text-[10px]">Small (10px)</option>
                        <option value="text-xs">Medium (12px)</option>
                        <option value="text-sm">Large (14px)</option>
                        <option value="text-base">Extra Large (16px)</option>
                     </select>
                  </div>
                </div>
              </div>

              {/* --- FOOTER SETTINGS --- */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-slate-800 border-b border-slate-100 pb-2 flex items-center gap-2">
                  <LayoutTemplate size={16} className="text-indigo-500 rotate-180" /> Footer (Onderkant) Instellingen
                </h3>

                <div className="grid grid-cols-2 gap-6 pb-2">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Footer Achtergrond</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={appConfig.footerColor || '#000000'}
                        onChange={e => setAppConfig(prev => ({ ...prev, footerColor: e.target.value }))}
                        className="w-10 h-10 rounded-lg cursor-pointer border shadow-sm p-1 bg-white shrink-0"
                      />
                      <input
                        type="text"
                        value={appConfig.footerColor || '#000000'}
                        onChange={e => setAppConfig(prev => ({ ...prev, footerColor: e.target.value }))}
                        className="flex-1 border border-slate-200 rounded-lg p-2.5 text-sm uppercase font-mono focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Footer Tekstkleur</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={appConfig.footerTextColor || '#ffffff'}
                        onChange={e => setAppConfig(prev => ({ ...prev, footerTextColor: e.target.value }))}
                        className="w-10 h-10 rounded-lg cursor-pointer border shadow-sm p-1 bg-white shrink-0"
                      />
                      <input
                        type="text"
                        value={appConfig.footerTextColor || '#ffffff'}
                        onChange={e => setAppConfig(prev => ({ ...prev, footerTextColor: e.target.value }))}
                        className="flex-1 border border-slate-200 rounded-lg p-2.5 text-sm uppercase font-mono focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6 pb-2">
                  <div className="space-y-1.5">
                     <label className="text-[10px] font-bold text-slate-500 uppercase">Footer Lettertype</label>
                     <select value={appConfig.footerFont || ''} onChange={e => setAppConfig(prev => ({...prev, footerFont: e.target.value}))} className="w-full border border-slate-200 rounded-lg p-2 text-xs">
                        <option value="">-- Main Font --</option>
                        <option value="Roboto, sans-serif">Roboto</option>
                        <option value="Inter, sans-serif">Inter</option>
                        <option value="Montserrat, sans-serif">Montserrat</option>
                        <option value="Open Sans, sans-serif">Open Sans</option>
                     </select>
                  </div>
                  <div className="space-y-1.5">
                     <label className="text-[10px] font-bold text-slate-500 uppercase">Footer Text Formaat</label>
                     <select value={appConfig.footerFontSize || 'text-xs'} onChange={e => setAppConfig(prev => ({...prev, footerFontSize: e.target.value}))} className="w-full border border-slate-200 rounded-lg p-2 text-xs">
                        <option value="text-[10px]">Small (10px)</option>
                        <option value="text-xs">Medium (12px)</option>
                        <option value="text-sm">Large (14px)</option>
                     </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Titel Navigatie Kolom</label>
                    <input
                      type="text"
                      placeholder="Bv. Navigatie"
                      value={appConfig.footerConfig?.navTitle || ''}
                      onChange={e => setAppConfig(prev => ({ ...prev, footerConfig: { ...prev.footerConfig, navTitle: e.target.value } }))}
                      className="w-full border border-slate-200 rounded-md p-2 text-xs focus:border-indigo-500 outline-none"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Titel Contact Kolom</label>
                    <input
                      type="text"
                      placeholder="Bv. Contact"
                      value={appConfig.footerConfig?.contactTitle || ''}
                      onChange={e => setAppConfig(prev => ({ ...prev, footerConfig: { ...prev.footerConfig, contactTitle: e.target.value } }))}
                      className="w-full border border-slate-200 rounded-md p-2 text-xs focus:border-indigo-500 outline-none"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Titel Socials Kolom</label>
                    <input
                      type="text"
                      placeholder="Bv. Volg Ons"
                      value={appConfig.footerConfig?.socialTitle || ''}
                      onChange={e => setAppConfig(prev => ({ ...prev, footerConfig: { ...prev.footerConfig, socialTitle: e.target.value } }))}
                      className="w-full border border-slate-200 rounded-md p-2 text-xs focus:border-indigo-500 outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-1.5 pt-4">
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wide flex items-center justify-between">
                    <span>Over Ons (Footer Tekst)</span>
                  </label>
                  <textarea
                    placeholder="Wij bouwen aan de toekomst..."
                    value={appConfig.footerConfig?.copyrightText || ''}
                    onChange={e => setAppConfig(prev => ({ ...prev, footerConfig: { ...prev.footerConfig, copyrightText: e.target.value } }))}
                    className="w-full border border-slate-200 rounded-lg p-2.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none min-h-[80px] resize-y"
                  />
                  <p className="text-[10px] text-slate-400">Deze tekst verschijnt links onder jullie logo/bedrijfsnaam.</p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
              <button
                onClick={() => setShowFooterEditor(false)}
                className="px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold shadow-sm hover:bg-indigo-700 transition-colors"
              >
                Gereed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareersSiteEditor;
